import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import contentPT from './data/content.pt.json';
import contentEN from './data/content.en.json';
import { Link } from 'react-router-dom';
import ReactPlayer from 'react-player/youtube';

// Helper function to shuffle array
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function App() {
  const lang = 'en';
  const content = contentEN;
  // Randomize hero data on component mount
  const [heroData] = useState(() => shuffleArray(content.hero || []));
  const [activeIndex, setActiveIndex] = useState(0);
  const currentItem = heroData?.[activeIndex] || {};
  const currentId = currentItem?.VideoId || '';
  const scrollLockRef = useRef(false);
  const [isMuted, setIsMuted] = useState(true); // Always start muted for Safari autoplay
  const playerRef = useRef(null);
  const dragStartRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // When language/content changes, reset active video to the first item
  useEffect(() => {
    // reset to first slide when language changes
    setActiveIndex(0);
  }, [lang]);

  const handleWheel = (e) => {
    if (scrollLockRef.current || heroData.length === 0) return;
    
    const deltaY = e.deltaY;
    const deltaX = e.deltaX;
    
    // Check if this is a horizontal swipe (Mac trackpad gesture)
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 20;
    
    if (isHorizontalSwipe) {
      // Horizontal swipe detected
      let next = activeIndex;
      if (deltaX > 0) {
        // Swiping left (going to next)
        next = activeIndex + 1 >= heroData.length ? 0 : activeIndex + 1;
      } else if (deltaX < 0) {
        // Swiping right (going to previous)
        next = activeIndex - 1 < 0 ? heroData.length - 1 : activeIndex - 1;
      }
      
      if (next !== activeIndex) {
        setActiveIndex(next);
        scrollLockRef.current = true;
        setTimeout(() => {
          scrollLockRef.current = false;
        }, 700);
      }
      return;
    }
    
    // Vertical scroll
    const delta = deltaY;
    if (Math.abs(delta) < 20) return; // ignore tiny scrolls

    let next = activeIndex;
    if (delta > 0) {
      // Going forward: wrap to 0 if at the end
      next = activeIndex + 1 >= heroData.length ? 0 : activeIndex + 1;
    } else if (delta < 0) {
      // Going backward: wrap to last if at the beginning
      next = activeIndex - 1 < 0 ? heroData.length - 1 : activeIndex - 1;
    }

    if (next !== activeIndex) {
      setActiveIndex(next);
      // lock for a short time to prevent overscroll skipping multiple items
      scrollLockRef.current = true;
      setTimeout(() => {
        scrollLockRef.current = false;
      }, 700);
    }
  };

  // Mobile tap zones: left = previous, right = next
  const goPrev = () => {
    setActiveIndex((idx) => idx - 1 < 0 ? heroData.length - 1 : idx - 1);
  };
  const goNext = () => {
    setActiveIndex((idx) => idx + 1 >= heroData.length ? 0 : idx + 1);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const openVideoInYouTube = () => {
    if (currentId) {
      window.open(`https://www.youtube.com/watch?v=${currentId}`, '_blank');
    }
  };

  // Swipe/Drag handlers
  const handleDragStart = (clientX) => {
    if (scrollLockRef.current) return;
    dragStartRef.current = clientX;
    setIsDragging(true);
  };

  const handleDragEnd = (clientX) => {
    if (!isDragging || dragStartRef.current === null) {
      setIsDragging(false);
      return;
    }
    
    const diff = clientX - dragStartRef.current;
    const threshold = 50; // minimum pixels to trigger navigation

    if (Math.abs(diff) > threshold && !scrollLockRef.current) {
      if (diff > 0) {
        // Dragged right -> go to previous
        goPrev();
      } else {
        // Dragged left -> go to next
        goNext();
      }
      // Lock scroll after drag navigation
      scrollLockRef.current = true;
      setTimeout(() => {
        scrollLockRef.current = false;
      }, 700);
    }

    dragStartRef.current = null;
    setIsDragging(false);
  };

  // Mouse events
  const handleMouseDown = (e) => {
    handleDragStart(e.clientX);
  };

  const handleMouseUp = (e) => {
    handleDragEnd(e.clientX);
  };

  const handleMouseLeave = () => {
    dragStartRef.current = null;
    setIsDragging(false);
  };

  // Touch events
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      handleDragStart(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = (e) => {
    if (e.changedTouches.length === 1) {
      handleDragEnd(e.changedTouches[0].clientX);
    }
  };

  return (
    <div 
      className="Landing" 
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background Video using YouTube IFrame API to force highest quality */}
      <BackgroundVideo videoId={currentId} playerRef={playerRef} isMuted={isMuted} />

      {/* Clickable overlay to navigate to project */}
      {currentItem?.projectId && (
        <Link to={`/projects?video=${currentItem.projectId}`} className="video-clickable-overlay" aria-label="View project details" />
      )}

      <Navbar brand={content.brand} showVolumeToggle onVolumeToggle={toggleMute} isMuted={isMuted} />

      {/* Bottom row: caption only, aligned by bottom */}
      <div className="hero-bottom-row">
        <div className="hero-caption">
          <div className="hero-caption-title">{currentItem?.Title}</div>
          <div className="hero-caption-sub">{currentItem?.subtitle}</div>
          {currentItem?.director && (
            <div className="hero-caption-director">{currentItem.director}</div>
          )}
        </div>
      </div>


      {/* Mobile touch zones for carousel navigation (shown only on small screens via CSS) */}
      <div
        className="touch-zone touch-zone-left"
        onClick={goPrev}
        aria-label="Previous slide"
        role="button"
        tabIndex={-1}
      />
      <div
        className="touch-zone touch-zone-right"
        onClick={goNext}
        aria-label="Next slide"
        role="button"
        tabIndex={-1}
      />

      {/* Carousel navigation arrows - only show if more than 1 slide */}
      {heroData.length > 1 && (
        <>
          <button
            className="carousel-arrow carousel-arrow-left"
            onClick={goPrev}
            aria-label="Previous slide"
          >
            ‹
          </button>
          <button
            className="carousel-arrow carousel-arrow-right"
            onClick={goNext}
            aria-label="Next slide"
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}

export default App;

// Components

// Background YouTube player using ReactPlayer
function BackgroundVideo({ videoId, playerRef, isMuted }) {
  const [coverVisible, setCoverVisible] = useState(true);
  const [key, setKey] = useState(0);

  // Detect Safari
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Force remount when videoId changes to ensure Safari plays the new video
  useEffect(() => {
    setCoverVisible(true);
    setKey(prev => prev + 1);
  }, [videoId]);

  const handleReady = (player) => {
    playerRef.current = player;
    
    const internalPlayer = player.getInternalPlayer();
    
    // Force mute on the YouTube player for Safari autoplay
    if (internalPlayer && internalPlayer.mute) {
      internalPlayer.mute();
    }
    
    // For Safari, aggressively try to start playback
    if (isSafari && internalPlayer) {
      // First attempt immediately
      if (internalPlayer.playVideo) {
        internalPlayer.playVideo();
      }
      
      // Second attempt after 100ms
      setTimeout(() => {
        if (internalPlayer.playVideo) {
          internalPlayer.playVideo();
        }
      }, 100);
      
      // Third attempt after 500ms
      setTimeout(() => {
        if (internalPlayer.playVideo) {
          internalPlayer.playVideo();
        }
      }, 500);
    }
    
    // Hide cover after player is ready
    setTimeout(() => setCoverVisible(false), 250);
  };

  const handleStart = () => {
    // Ensure video starts playing on Safari
    if (isSafari && playerRef.current) {
      const internalPlayer = playerRef.current.getInternalPlayer();
      if (internalPlayer && internalPlayer.playVideo) {
        internalPlayer.playVideo();
      }
    }
  };

  return (
    <div className="video-bg">
      <div className="video-iframe">
        <ReactPlayer
          key={key}
          ref={playerRef}
          url={`https://www.youtube.com/watch?v=${videoId}`}
          playing={true}
          loop={true}
          muted={isMuted}
          volume={isMuted ? 0 : 0.5}
          width="100%"
          height="100%"
          onReady={handleReady}
          onStart={handleStart}
          config={{
            youtube: {
              playerVars: {
                autoplay: 1,
                controls: 0,
                rel: 0,
                modestbranding: 1,
                playsinline: 1,
                iv_load_policy: 3,
                disablekb: 1,
                fs: 0,
                showinfo: 0,
                cc_load_policy: 0,
                mute: 1, // Force mute for Safari autoplay
                enablejsapi: 1, // Enable JavaScript API for better control
              },
              embedOptions: {
                allow: 'autoplay; encrypted-media'
              }
            }
          }}
          playsinline={true}
        />
      </div>
      <div className={`video-cover ${coverVisible ? 'show' : 'hide'}`} aria-hidden="true" />
      <div className="video-overlay" />
    </div>
  );
}

function VolumeToggle({ isMuted, onToggle }) {
  return (
    <button
      className="volume-toggle"
      onClick={onToggle}
      aria-label={isMuted ? 'Unmute' : 'Mute'}
      title={isMuted ? 'Unmute' : 'Mute'}
    >
      {isMuted ? (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor"/>
          <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ) : (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor"/>
          <path d="M15.54 8.46a5 5 0 010 7.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M19.07 4.93a10 10 0 010 14.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      )}
    </button>
  );
}

export function Navbar({ brand, dark = false, navColor = 'white', scrolled = false, showVolumeToggle = false, onVolumeToggle, isMuted }) {
  const [open, setOpen] = useState(false);
  const sidebarRef = useRef(null);
  const triggerRef = useRef(null);
  const lang = 'en';

  useEffect(() => {
    const handleDocClick = (e) => {
      if (!open) return;
      const sidebarEl = sidebarRef.current;
      const triggerEl = triggerRef.current;
      const target = e.target;
      if (
        sidebarEl && !sidebarEl.contains(target) &&
        triggerEl && !triggerEl.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [open]);

  // On Projects page (dark navbar), push content when menu is open
  useEffect(() => {
    if (dark && open) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [dark, open]);

  // Use specific logo variants based on navbar text color
  const logoSrc = navColor === 'black'
    ? '/Magic-Fish-Logo-black.svg'
    : '/Magic-Fish-Logo-WhiteText.svg';

  return (
    <>
      <nav className={`navbar ${dark ? 'navbar--dark' : ''} ${navColor === 'black' ? 'navbar--text-black' : 'navbar--text-white'} ${scrolled ? 'navbar--scrolled' : ''}`}>
        <button
          className="menu-trigger"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          ref={triggerRef}
        >
          <HamburgerIcon />
        </button>
        <Link to="/" className="brand">
          <img src={logoSrc} alt={brand} className="brand-logo" />
        </Link>
        {showVolumeToggle && <VolumeToggle isMuted={isMuted} onToggle={onVolumeToggle} />}
      </nav>

      {/* Sidebar Drawer */}
      <div className={`sidebar ${open ? 'open' : ''} ${dark ? 'dark' : ''}`} ref={sidebarRef}>
        <ul className="sidebar-list">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/projects">Projects</Link></li>
          <li><Link to="/about">About</Link></li>
          <li><Link to="/contact">Contact</Link></li>
        </ul>
      </div>
      {/* Close when clicking outside area via Escape key would be added later if desired */}
    </>
  );
}

function HamburgerIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="6" width="18" height="2" fill="currentColor" />
      <rect x="3" y="11" width="18" height="2" fill="currentColor" />
      <rect x="3" y="16" width="18" height="2" fill="currentColor" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
